export type EventParam = Record<string, string | number | boolean>;

// Only these param keys are ever forwarded to Google Analytics. Anything else
// is dropped so a careless trackEvent call can never leak FIX message content
// or other sensitive user data. Keep values to ids/enums/counts/booleans only.
const ALLOWED_PARAM_KEYS = new Set([
  "action",
  "value",
  "count",
  "sample",
  "view",
  "lens",
  "column",
  "format",
  "theme",
  "mode",
  "source",
  "status",
  "type",
  "duration_ms",
]);

const MAX_STRING_LEN = 64;

// Heuristics that flag a string as possibly carrying FIX content or free-form
// user input (titles, tag values, file names). Such values are dropped rather
// than sent. Pure ids/enums (e.g. "diff", "35", "dark") pass cleanly.
const looksSensitive = (value: string): boolean =>
  value.length > MAX_STRING_LEN ||
  value.includes("\x01") || // FIX SOH delimiter
  value.includes("=") || // FIX tag=value pair
  value.includes("|") || // human-readable FIX separator
  /8=FIX/i.test(value);

const isDebug = (): boolean => {
  try {
    return window.localStorage?.getItem("ga_debug_mode") === "1";
  } catch {
    return false;
  }
};

// Enforce the privacy contract at runtime: drop unknown keys and any value that
// resembles user content. Exported for unit testing.
export const sanitizeParams = (params: EventParam): EventParam => {
  const safe: EventParam = {};
  for (const [key, value] of Object.entries(params)) {
    if (!ALLOWED_PARAM_KEYS.has(key)) {
      if (isDebug()) console.warn(`[gtag] dropped disallowed param "${key}"`);
      continue;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      safe[key] = value;
      continue;
    }
    if (typeof value === "string" && !looksSensitive(value)) {
      safe[key] = value;
      continue;
    }
    if (isDebug()) {
      console.warn(`[gtag] dropped unsafe value for param "${key}"`);
    }
  }
  return safe;
};

export const trackEvent = (event_name: string, event_params: EventParam) => {
  try {
    // eslint-disable-next-line no-prototype-builtins
    if (window.hasOwnProperty("gtag")) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const gtag: any = window["gtag"]; // eslint-disable-line
      gtag("event", event_name, sanitizeParams(event_params));
    }
  } catch (e) {
    console.warn(e);
  }
};
